import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronLeft, Pencil, Trash2 } from "lucide-react";
import { Button, Card, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import {
  useCommunication,
  useDeleteCommunication,
  useUpdateCommunication,
} from "./hooks/useCommunications";
import { ChannelChip } from "./components/ChannelChip";
import { DirectionArrow } from "./components/DirectionArrow";
import { OutcomeBadge } from "./components/OutcomeBadge";
import { ParticipantList } from "./components/ParticipantList";
import { PlacementList } from "./components/PlacementList";
import { EntryActivityLog } from "./components/EntryActivityLog";
import { formatDateTime, relativeTime } from "@/components/ux/relativeTime";
import { useDateFormatter } from "@/utils/datetime";

export function CommunicationDetailPage() {
  const { tz } = useDateFormatter();
  const { id } = useParams<{ id: string }>();
  const entryId = id ?? "";
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { toast } = useToast();

  const entryQ = useCommunication(entryId);
  const update = useUpdateCommunication(entryId);
  const del = useDeleteCommunication();

  const [bodyEditing, setBodyEditing] = useState(false);
  const [bodyDraft, setBodyDraft] = useState("");

  useEffect(() => {
    if (entryQ.data) setBodyDraft(entryQ.data.body);
  }, [entryQ.data]);

  if (entryQ.isLoading) {
    return <div className="text-small text-gray-mid">Loading entry…</div>;
  }
  if (entryQ.isError) {
    return (
      <div className="text-small text-status-error">
        Could not load entry. {(entryQ.error as Error).message}
      </div>
    );
  }
  const entry = entryQ.data;
  if (!entry) return null;

  const canEdit =
    hasPermission("Communications.ViewAll") ||
    user?.id === entry.loggedByUserId;

  const handleErr = (err: unknown, fallback: string) => {
    const msg = err instanceof ApiError ? err.errors?.[0] ?? err.message : fallback;
    toast({ message: msg, tone: "error" });
  };

  const saveBody = async () => {
    const next = bodyDraft.trim();
    if (!next || next === entry.body) {
      setBodyEditing(false);
      setBodyDraft(entry.body);
      return;
    }
    try {
      await update.mutateAsync({ body: next });
      setBodyEditing(false);
      toast({ message: "Notes updated.", tone: "success" });
    } catch (e) {
      handleErr(e, "Could not update notes.");
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Soft-delete this communication entry? Compliance can restore it.")) return;
    try {
      await del.mutateAsync(entryId);
      toast({ message: "Entry deleted.", tone: "success" });
      navigate("/communications");
    } catch (e) {
      handleErr(e, "Could not delete entry.");
    }
  };

  return (
    <div className="flex flex-col gap-space-4">
      <div className="text-label text-gray-mid">
        <Link
          to="/communications"
          className="inline-flex items-center gap-space-1 hover:text-onyx"
        >
          <ChevronLeft size={14} aria-hidden /> Back to communications
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-space-4">
        <Card>
          <div className="flex items-center justify-between gap-space-3 mb-space-3">
            <div className="flex items-center gap-space-2">
              <ChannelChip channel={entry.channel} />
              <DirectionArrow direction={entry.direction} size={16} />
              {entry.outcome && (
                <OutcomeBadge outcome={entry.outcome} />
              )}
            </div>
            <span
              className="text-label text-gray-mid tabular-nums"
              title={formatDateTime(entry.occurredAt, tz)}
            >
              {relativeTime(entry.occurredAt)}
            </span>
          </div>

          {entry.subject && (
            <h1 className="font-display font-extrabold text-h2 text-onyx m-0 mb-space-3">
              {entry.subject}
            </h1>
          )}

          <div className="mb-space-4">
            {bodyEditing ? (
              <div className="flex flex-col gap-space-2">
                <Textarea
                  value={bodyDraft}
                  onChange={(e) => setBodyDraft(e.target.value)}
                  rows={8}
                  aria-label="Notes"
                />
                <div className="flex justify-end gap-space-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      setBodyDraft(entry.body);
                      setBodyEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                    onClick={saveBody}
                    loading={update.isPending}
                  >
                    Save
                  </Button>
                </div>
              </div>
            ) : canEdit ? (
              <button
                type="button"
                onClick={() => setBodyEditing(true)}
                className="text-left w-full prose prose-sm max-w-none text-onyx hover:bg-white-warm rounded-md p-space-2 -m-space-2"
                aria-label="Edit notes"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {entry.body}
                </ReactMarkdown>
              </button>
            ) : (
              <div className="prose prose-sm max-w-none text-onyx">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {entry.body}
                </ReactMarkdown>
              </div>
            )}
          </div>

          <hr className="border-0 border-t border-border-light my-space-3" />

          <ParticipantList
            entryId={entry.id}
            participants={entry.participants}
            canEdit={canEdit}
          />

          <hr className="border-0 border-t border-border-light my-space-3" />

          <PlacementList
            entryId={entry.id}
            placements={entry.placements}
            canEdit={canEdit}
          />

          <hr className="border-0 border-t border-border-light my-space-3" />

          <EntryActivityLog entryId={entry.id} />
        </Card>

        <aside>
          <Card>
            <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
              Entry metadata
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-space-3 gap-y-space-2 text-small">
              <dt className="text-gray-mid">Logged by</dt>
              <dd className="text-onyx">{entry.loggedByDisplayName}</dd>

              <dt className="text-gray-mid">Logged at</dt>
              <dd className="text-onyx">{formatDateTime(entry.loggedAt, tz)}</dd>

              <dt className="text-gray-mid">Occurred at</dt>
              <dd className="text-onyx">{formatDateTime(entry.occurredAt, tz)}</dd>

              {entry.durationMinutes != null && (
                <>
                  <dt className="text-gray-mid">Duration</dt>
                  <dd className="text-onyx">{entry.durationMinutes} min</dd>
                </>
              )}
              {entry.modality && (
                <>
                  <dt className="text-gray-mid">Modality</dt>
                  <dd className="text-onyx">{entry.modality}</dd>
                </>
              )}
              {entry.externalPhoneNumber && (
                <>
                  <dt className="text-gray-mid">Phone</dt>
                  <dd className="text-onyx">{entry.externalPhoneNumber}</dd>
                </>
              )}
              {entry.emailFromAddress && (
                <>
                  <dt className="text-gray-mid">From</dt>
                  <dd className="text-onyx break-all">
                    {entry.emailFromAddress}
                  </dd>
                </>
              )}
              {entry.emailToAddress && (
                <>
                  <dt className="text-gray-mid">To</dt>
                  <dd className="text-onyx break-all">
                    {entry.emailToAddress}
                  </dd>
                </>
              )}
            </dl>

            {canEdit && (
              <>
                <hr className="border-0 border-t border-border-light my-space-3" />
                <div className="flex items-center justify-between gap-space-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={() => setBodyEditing(true)}
                  >
                    <Pencil size={14} aria-hidden /> Edit notes
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={onDelete}
                    loading={del.isPending}
                  >
                    <Trash2 size={14} aria-hidden /> Delete
                  </Button>
                </div>
              </>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
}
